import EmploymentFormModal, { EmploymentFormData } from "./EmploymentFormModal";
import {
  EMPLOYMENT_FRAGMENT,
  EmploymentRow,
  GET_EMPLOYMENT_DETAILS_QUERY,
  GET_JOB_TITLES_QUERY,
  JobTitle
} from "../../person/queries";
import { ApolloCache, useApolloClient, useMutation, useQuery } from "@apollo/client";
import { UPDATE_EMPLOYMENT_MUTATION } from "../../person/mutations";
import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import { useParams } from "@/router";
import { PollForBandChangesFunction } from "@/hooks/usePollBandChanges";
import { Band } from "@/types";

interface EditEmploymentModalProps {
  employmentId: string;
  onClose(): void;
  personId: string;
  pollForBandChanges: PollForBandChangesFunction;
  fetchCurrentBands: (pgaId: string) => Promise<Band[]>;
}

function loadEmployment(cache: ApolloCache<object>, id: string) {
  const data = cache.readFragment<EmploymentRow>({
    fragment: EMPLOYMENT_FRAGMENT,
    fragmentName: "EmploymentFragment",
    id: cache.identify({ __typename: "BackofficeEmployment", id })
  });

  return data;
}

function toProps(employment: EmploymentRow, approvedJobTitles: JobTitle[]) {
  let availableJobTitles = approvedJobTitles;

  let classification = approvedJobTitles.find(
    ({ jobTitle, jobClassification }) =>
      jobTitle === employment.title && jobClassification === employment.jobClassification
  );

  if (!classification) {
    classification = {
      jobTitle: employment.title,
      jobClassification: employment.jobClassification ?? "",
      id: "unapproved-classification-id"
    };

    availableJobTitles = [classification, ...approvedJobTitles];
  }

  return {
    values: {
      endDate: employment.endedOn || "",
      facilityId: employment.facility.id,
      isPrimary: employment.isPrimary,
      startDate: employment.startedOn ?? "",
      workingLocation: employment.workingLocation,
      tourCode: employment.tourCode,
      classification: classification.id,
      note: ""
    },
    availableJobTitles
  };
}

export default function EditEmploymentModal({
  employmentId,
  personId,
  onClose,
  pollForBandChanges,
  fetchCurrentBands
}: EditEmploymentModalProps) {
  const { pgaId } = useParams("/people/:pgaId/career/employment");

  const [updateEmployment] = useMutation(UPDATE_EMPLOYMENT_MUTATION, {
    refetchQueries: [
      {
        query: GET_EMPLOYMENT_DETAILS_QUERY,
        variables: { pgaId }
      },
      {
        query: PERSON_DETAILS_HEADER_QUERY,
        variables: { pgaId }
      }
    ],
    awaitRefetchQueries: true
  });

  const { cache } = useApolloClient();
  const employment = loadEmployment(cache, employmentId);

  async function onSubmit(formData: EmploymentFormData) {
    // Capture baseline BEFORE mutation to ensure we detect changes
    let baselineBands: Band[] | undefined;

    try {
      baselineBands = await fetchCurrentBands(pgaId);
    } catch (e) {
      console.warn("[EditEmployment] Failed to capture baseline, will capture during polling:", e);
    }

    const { note, employmentsToEnd, ...mutationData } = formData;
    const noteInput = note ? { content: note, priority: "NONE", title: "Edit Employment" } : undefined;
    const { data } = await updateEmployment({
      variables: {
        input: {
          ...mutationData,
          id: employment!.id,
          note: noteInput
        }
      }
    });

    const result = data?.updateEmployment || { success: false, message: "Failed to update employment" };

    // If mutation succeeded, poll for band changes
    // Use pgaId from route params since the query requires pgaId, not person.id
    if (result.success) {
      const facilitySectionLegacyId = employment?.facility?.section?.legacyId;

      const expectedChanges = {
        classificationName: formData.jobClassification,
        startedOn: formData.startedOn,
        sectionLegacyId: facilitySectionLegacyId
      };

      // Poll for band changes in the background, passing baseline if we captured it
      pollForBandChanges(pgaId, expectedChanges, baselineBands).catch((error) => {
        console.error("[EditEmployment] Error polling for band changes:", error);
      });
    }

    return result;
  }

  const { data } = useQuery(GET_JOB_TITLES_QUERY, {
    variables: { personId }
  });

  let availableJobTitles = data?.person.availableJobTitles || [];

  return (
    <EmploymentFormModal
      open
      onClose={onClose}
      {...toProps(employment!, availableJobTitles)}
      tourCodes={data?.tourCodes || []}
      headline="Edit Employment"
      onSubmit={onSubmit}
    />
  );
}
